import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/getApiUrl.ts';
import { isBackendOnline, isBackendOnlineSync, forceBackendCheck } from '../utils/backendStatus.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { dbGetAll, dbClear, dbAdd } from '../services/db/indexedDb.ts';
import { performSync } from '../services/syncService.ts';
import { listAllSyncItems } from '../services/db/repositories/syncQueue.ts';
import { processSyncQueue } from '../services/syncQueue.ts';
import { useNotifications } from '../hooks/useNotifications.ts';
import { APP_VERSION, SW_CACHE_VERSION } from '../utils/version.ts';
import './Settings.css';

const Settings: React.FC = () => {
  const [debugInfo, setDebugInfo] = useState<any>({});
  const [logs, setLogs] = useState<string[]>([]);
  const [testResults, setTestResults] = useState<any>({});
  const [message, setMessage] = useState<string>('');
  const [lastSync, setLastSync] = useState<string | null>(localStorage.getItem('last_sync_time'));
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncQueueItems, setSyncQueueItems] = useState<any[]>([]);
  const [isQueueLoading, setIsQueueLoading] = useState(false);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const [queueMessage, setQueueMessage] = useState<string>('');
  const [exportingData, setExportingData] = useState(false);
  const [clearingDatabase, setClearingDatabase] = useState(false);
  const [updatingPWA, setUpdatingPWA] = useState(false);
  const [brapiStatus, setBrapiStatus] = useState<{ status: string; message: string; configured?: boolean; latency_ms?: number } | null>(null);
  const [checkingBrapi, setCheckingBrapi] = useState(false);
  
  const { user, isAuthenticated } = useAuth();
  const {
    isSupported: notificationsSupported,
    permission: notificationPermission,
    isEnabled: notificationsEnabled,
    hasPermission: hasNotificationPermission,
    requestPermission: requestNotificationPermission,
    setEnabled: setNotificationsEnabled,
  } = useNotifications();

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [`[${timestamp}] ${message}`, ...prev].slice(0, 50));
  };

  const testBackendHealth = async () => {
    addLog('Testando health check do backend...');
    const apiUrl = getApiBaseUrl();
    addLog(`URL da API: ${apiUrl}`);
    
    try {
      const response = await fetch(`${apiUrl}/health`, {
        method: 'GET',
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000)
      });
      
      const data = await response.text();
      addLog(`Health check: ${response.status} - ${data}`);
      setTestResults(prev => ({ ...prev, healthCheck: { success: response.ok, status: response.status, data } }));
    } catch (error: any) {
      addLog(`Erro no health check: ${error.message}`);
      setTestResults(prev => ({ ...prev, healthCheck: { success: false, error: error.message } }));
    }
  };

  const testBackendLogin = async () => {
    addLog('Testando login no backend...');
    const apiUrl = getApiBaseUrl();
    
    try {
      const response = await fetch(`${apiUrl}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: 'admin@admin.com', password: 'admin' }),
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000)
      });
      
      const data = await response.json();
      addLog(`Login: ${response.status} - ${response.ok ? 'SUCESSO' : 'FALHOU'}`);
      setTestResults(prev => ({ ...prev, login: { success: response.ok, status: response.status, hasToken: !!data.token } }));
    } catch (error: any) {
      addLog(`Erro no login: ${error.message}`);
      setTestResults(prev => ({ ...prev, login: { success: false, error: error.message } }));
    }
  };

  const testBackendStatus = async () => {
    addLog('Verificando status do backend...');
    const syncStatus = isBackendOnlineSync();
    const asyncStatus = await isBackendOnline();
    addLog(`Backend status (sync): ${syncStatus}`);
    addLog(`Backend status (async): ${asyncStatus}`);
    setTestResults(prev => ({ ...prev, backendStatus: { sync: syncStatus, async: asyncStatus } }));
  };

  const forceBackendStatusCheck = async () => {
    addLog('Forçando verificação do backend...');
    const isOnline = await forceBackendCheck((status) => {
      addLog(`Backend status atualizado: ${status ? 'ONLINE' : 'OFFLINE'}`);
    });
    addLog(`Resultado da verificação forçada: ${isOnline ? 'ONLINE' : 'OFFLINE'}`);
    setTestResults(prev => ({ ...prev, forcedCheck: { success: isOnline, timestamp: new Date().toISOString() } }));
  };

  const checkBrapiHealth = async () => {
    setCheckingBrapi(true);
    try {
      const apiUrl = getApiBaseUrl();
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/v1/system/brapi-health`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-cache',
        signal: AbortSignal.timeout(15000)
      });
      const data = await response.json();
      setBrapiStatus(data);
      addLog(`Brapi health: ${data.status} - ${data.message}`);
    } catch (error: any) {
      setBrapiStatus({ status: 'error', message: error.message });
      addLog(`Erro ao verificar Brapi: ${error.message}`);
    } finally {
      setCheckingBrapi(false);
    }
  };

  const collectDebugInfo = () => {
    const token = localStorage.getItem('token');
    const info = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      onLine: navigator.onLine,
      apiBaseUrl: getApiBaseUrl(),
      backendOnlineSync: isBackendOnlineSync(),
      hasToken: !!token,
      // Não expor token, nem parcialmente, por segurança
      tokenLength: token ? token.length : 0,
      // Não expor dados do usuário offline completo
      hasOfflineUser: !!localStorage.getItem('offline_user'),
      backendIp: localStorage.getItem('backend_ip'),
      isAuthenticated,
      user: user ? { id: user.id, email: user.email, name: user.name } : null,
      windowLocation: {
        hostname: window.location.hostname,
        port: window.location.port,
        protocol: window.location.protocol,
        // Não expor href completo por segurança
        pathname: window.location.pathname
      }
    };
    setDebugInfo(info);
    addLog('Informações de debug coletadas');
  };

  const exportData = async () => {
    setExportingData(true);
    try {
      addLog('Iniciando exportação de dados...');
      const [users, accounts, categories, transactions, meta] = await Promise.all([
        dbGetAll('users'), 
        dbGetAll('accounts'), 
        dbGetAll('categories'), 
        dbGetAll('transactions'), 
        dbGetAll('meta')
      ]);
      const payload = { 
        users, 
        accounts, 
        categories, 
        transactions, 
        meta, 
        exported_at: new Date().toISOString(), 
        version: 1 
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `prumo-backup-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage('✅ Dados exportados com sucesso!');
      addLog('Exportação concluída com sucesso');
    } catch (error: any) {
      setMessage(`❌ Erro ao exportar: ${error.message}`);
      addLog(`Erro na exportação: ${error.message}`);
    } finally {
      setExportingData(false);
    }
  };

  const importData = async (file: File) => {
    try {
      addLog('Iniciando importação de dados...');
      setMessage('Importando dados...');
      const text = await file.text();
      
      // Validar tamanho do arquivo (máximo 10MB)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('Arquivo muito grande. Tamanho máximo: 10MB');
      }
      
      // Validar que é JSON válido antes de parsear
      let json;
      try {
        json = JSON.parse(text);
      } catch (parseError) {
        throw new Error('Arquivo não é um JSON válido');
      }
      
      // Validação de estrutura
      if (!json || typeof json !== 'object') {
        throw new Error('Arquivo inválido: não é um objeto JSON');
      }
      
      // Validar que tem pelo menos uma das estruturas esperadas
      if (!json.accounts && !json.categories && !json.transactions) {
        throw new Error('Arquivo inválido: não contém dados esperados (accounts, categories, transactions)');
      }
      
      // Validar que arrays são realmente arrays
      if (json.accounts && !Array.isArray(json.accounts)) {
        throw new Error('Arquivo inválido: accounts deve ser um array');
      }
      if (json.categories && !Array.isArray(json.categories)) {
        throw new Error('Arquivo inválido: categories deve ser um array');
      }
      if (json.transactions && !Array.isArray(json.transactions)) {
        throw new Error('Arquivo inválido: transactions deve ser um array');
      }
      
      // Limpar dados existentes
      await Promise.all(['users', 'accounts', 'categories', 'transactions', 'meta'].map(s => dbClear(s as any)));
      addLog('Dados antigos limpos');
      
      // Importar novos dados
      for (const u of json.users || []) {
        await dbAdd('users' as any, u);
      }
      for (const a of json.accounts || []) {
        await dbAdd('accounts' as any, a);
      }
      for (const c of json.categories || []) {
        await dbAdd('categories' as any, c);
      }
      for (const t of json.transactions || []) {
        await dbAdd('transactions' as any, t);
      }
      if (json.meta) {
        for (const m of json.meta) {
          await dbAdd('meta' as any, m);
        }
      }
      
      setMessage('✅ Importação concluída com sucesso!');
      addLog('Importação concluída com sucesso');
      // Recarregar página após 2 segundos para refletir mudanças
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (e: any) {
      setMessage(`❌ Erro ao importar: ${e.message || e}`);
      addLog(`Erro na importação: ${e.message || e}`);
    }
  };

  const clearDatabase = async () => {
    if (!window.confirm('Tem certeza que deseja limpar todos os dados locais? Esta ação não pode ser desfeita!')) {
      return;
    }
    setClearingDatabase(true);
    try {
      addLog('Limpando base de dados local...');
      await Promise.all(['users', 'accounts', 'categories', 'transactions', 'meta'].map(s => dbClear(s as any)));
      setMessage('✅ Base de dados limpa com sucesso!');
      addLog('Base de dados limpa');
      // Recarregar página após 2 segundos
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      setMessage(`❌ Erro ao limpar: ${error.message}`);
      addLog(`Erro ao limpar: ${error.message}`);
    } finally {
      setClearingDatabase(false);
    }
  };

  const handleSyncNow = async () => {
    if (!navigator.onLine) {
      setSyncError('Sem conexão com internet');
      addLog('Erro: Sem conexão com internet');
      return;
    }

    const backendOnline = await isBackendOnline();
    if (!backendOnline) {
      setSyncError('Backend não está acessível');
      addLog('Erro: Backend não está acessível');
      return;
    }

    if (!localStorage.getItem('token')) {
      setSyncError('Faça login para sincronizar');
      addLog('Erro: Token não encontrado');
      return;
    }

    setIsSyncing(true);
    setSyncError(null);
    addLog('Iniciando sincronização...');

    try {
      const result = await performSync();
      if (result.success) {
        const now = new Date().toISOString();
        setLastSync(now);
        localStorage.setItem('last_sync_time', now);
        setSyncError(null);
        setMessage('✅ Sincronização concluída com sucesso!');
        addLog('Sincronização concluída com sucesso');
      } else {
        setSyncError(result.reason || 'Erro ao sincronizar');
        setMessage(`❌ Erro na sincronização: ${result.reason || 'Erro desconhecido'}`);
        addLog(`Erro na sincronização: ${result.reason || 'Erro desconhecido'}`);
      }
    } catch (error: any) {
      setSyncError(error.message || 'Erro desconhecido');
      setMessage(`❌ Erro na sincronização: ${error.message || 'Erro desconhecido'}`);
      addLog(`Erro na sincronização: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleUpdatePWA = async () => {
    if ('serviceWorker' in navigator) {
      setUpdatingPWA(true);
      try {
        addLog('Verificando atualizações do aplicativo...');
        const registration = await navigator.serviceWorker.ready;
        await registration.update();
        addLog('Atualização verificada. Se houver nova versão, você será notificado.');
        setMessage('✅ Verificação de atualização concluída. Se houver nova versão, você será notificado.');
      } catch (error: any) {
        addLog(`Erro ao verificar atualização: ${error.message}`);
        setMessage(`❌ Erro ao verificar atualização: ${error.message}`);
      } finally {
        setUpdatingPWA(false);
      }
    } else {
      setMessage('❌ Service Worker não suportado neste navegador.');
    }
  };

  const loadSyncQueue = async () => {
    setIsQueueLoading(true);
    try {
      const items = await listAllSyncItems();
      setSyncQueueItems(items);
      setQueueMessage('');
    } catch (error: any) {
      setQueueMessage(`❌ Erro ao carregar fila: ${error.message || error}`);
    } finally {
      setIsQueueLoading(false);
    }
  };

  const handleProcessQueue = async () => {
    setIsQueueLoading(true);
    setIsProcessingQueue(true);
    try {
      const result = await processSyncQueue();
      if (!result.success) {
        setQueueMessage(`❌ Erro ao processar fila: ${result.reason || 'Erro desconhecido'}`);
      } else {
        setQueueMessage('✅ Fila processada com sucesso.');
      }
      await loadSyncQueue();
    } catch (error: any) {
      setQueueMessage(`❌ Erro ao processar fila: ${error.message || error}`);
    } finally {
      setIsQueueLoading(false);
      setIsProcessingQueue(false);
    }
  };

  useEffect(() => {
    collectDebugInfo();
    testBackendStatus();
    loadSyncQueue();
    // Atualizar última sincronização quando mudar
    const syncTime = localStorage.getItem('last_sync_time');
    if (syncTime) {
      setLastSync(syncTime);
    }
    // Listener para atualizar quando sincronização ocorrer em outro lugar
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'last_sync_time') {
        setLastSync(e.newValue);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="settings-container">
      <h1>⚙️ Configurações</h1>
      
      {/* Seção de Backup e Restauração */}
      <div className="settings-section">
        <h2>💾 Backup e Restauração</h2>
        <p className="section-description">Exporte ou importe seus dados locais (modo offline)</p>
        <div className="action-buttons">
          <button onClick={exportData} className="action-button primary" disabled={exportingData}>
            {exportingData ? 'Exportando...' : '📤 Exportar Dados'}
          </button>
          <label className="action-button secondary" style={{ cursor: 'pointer' }}>
            📥 Importar Dados
            <input 
              type="file" 
              accept="application/json" 
              style={{ display: 'none' }} 
              onChange={(e) => e.target.files && e.target.files[0] && importData(e.target.files[0])} 
            />
          </label>
          <button onClick={clearDatabase} className="action-button danger" disabled={clearingDatabase}>
            {clearingDatabase ? 'Limpando...' : '🗑️ Limpar Base de Dados'}
          </button>
        </div>
        {message && (
          <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}
      </div>

      {/* Seção de Debug - Informações do Sistema */}
      <div className="settings-section">
        <h2>📊 Informações do Sistema</h2>
        <div className="info-grid">
          <div className="info-item">
            <strong>Status Online (navegador):</strong>
            <span className={navigator.onLine ? 'status-ok' : 'status-error'}>
              {navigator.onLine ? '✅ Online' : '❌ Offline'}
            </span>
          </div>
          <div className="info-item">
            <strong>Backend Online (sync):</strong>
            <span className={isBackendOnlineSync() ? 'status-ok' : 'status-error'}>
              {isBackendOnlineSync() ? '✅ Online' : '❌ Offline'}
            </span>
          </div>
          <div className="info-item">
            <strong>Autenticado:</strong>
            <span className={isAuthenticated ? 'status-ok' : 'status-error'}>
              {isAuthenticated ? '✅ Sim' : '❌ Não'}
            </span>
          </div>
          <div className="info-item">
            <strong>Token:</strong>
            <span className={localStorage.getItem('token') ? 'status-ok' : 'status-error'}>
              {localStorage.getItem('token') ? '✅ Presente' : '❌ Ausente'}
            </span>
          </div>
          <div className="info-item">
            <strong>Última Sincronização:</strong>
            <span>
              {lastSync 
                ? new Date(lastSync).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })
                : 'Nunca sincronizado'
              }
            </span>
          </div>
          <div className="info-item">
            <strong>Brapi API:</strong>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {brapiStatus === null ? (
                <span style={{ color: '#6b7280', fontStyle: 'italic' }}>Não verificado</span>
              ) : brapiStatus.status === 'ok' ? (
                <span className="status-ok">
                  ✅ OK{brapiStatus.latency_ms !== undefined ? ` (${brapiStatus.latency_ms}ms)` : ''}
                </span>
              ) : !brapiStatus.configured ? (
                <span className="status-error">❌ Token não configurado</span>
              ) : (
                <span className="status-error" title={brapiStatus.message}>❌ Erro</span>
              )}
              <button
                onClick={checkBrapiHealth}
                disabled={checkingBrapi || !isBackendOnlineSync()}
                style={{ fontSize: '12px', padding: '2px 8px', cursor: 'pointer' }}
                className="test-button"
              >
                {checkingBrapi ? '...' : '🔍'}
              </button>
            </span>
          </div>
          <div className="info-item">
            <strong>URL da API:</strong>
            <code>{getApiBaseUrl()}</code>
          </div>
          <div className="info-item">
            <strong>IP do Backend (salvo):</strong>
            <code>{localStorage.getItem('backend_ip') || 'Não configurado'}</code>
          </div>
          <div className="info-item">
            <strong>Hostname:</strong>
            <code>{window.location.hostname}</code>
          </div>
          <div className="info-item">
            <strong>Protocolo:</strong>
            <code>{window.location.protocol}</code>
          </div>
        </div>
        <div className="sync-actions" style={{ marginTop: '20px' }}>
          <button 
            onClick={handleSyncNow} 
            className="action-button primary"
            disabled={isSyncing || !navigator.onLine || !isBackendOnlineSync()}
          >
            {isSyncing ? '🔄 Sincronizando...' : '🔄 Sincronizar Agora'}
          </button>
          {syncError && (
            <div className="message error" style={{ marginTop: '10px' }}>
              {syncError}
            </div>
          )}
        </div>
      </div>

      {/* Seção de Fila de Sincronização */}
      <div className="settings-section">
        <h2>🔁 Fila de Sincronização</h2>
        <p className="section-description">
          Inspecione operações offline pendentes ou com falha.
        </p>
        <div className="action-buttons">
          <button
            onClick={loadSyncQueue}
            className="action-button secondary"
            disabled={isQueueLoading}
          >
            {isQueueLoading && !isProcessingQueue ? 'Atualizando...' : '🔄 Atualizar fila'}
          </button>
          <button
            onClick={handleProcessQueue}
            className="action-button primary"
            disabled={isQueueLoading || !navigator.onLine || !isBackendOnlineSync()}
          >
            {isProcessingQueue ? 'Processando...' : '▶️ Processar fila'}
          </button>
        </div>
        {queueMessage && (
          <div className={`message ${queueMessage.includes('✅') ? 'success' : 'error'}`}>
            {queueMessage}
          </div>
        )}
        <div className="sync-queue-summary">
          <span>Total: {syncQueueItems.length}</span>
          <span>Pendentes: {syncQueueItems.filter((item) => item.status === 'pending').length}</span>
          <span>Falhas: {syncQueueItems.filter((item) => item.status === 'failed').length}</span>
        </div>
        {syncQueueItems.length === 0 ? (
          <div className="sync-queue-empty">Nenhuma operação na fila.</div>
        ) : (
          <div className="sync-queue-table">
            <div className="sync-queue-row header">
              <span>ID</span>
              <span>Método</span>
              <span>Endpoint</span>
              <span>Status</span>
              <span>Tentativas</span>
              <span>Último erro</span>
            </div>
            {syncQueueItems.map((item) => (
              <div className="sync-queue-row" key={item.id}>
                <span>{item.id}</span>
                <span>{item.method}</span>
                <span className="sync-queue-url">{item.url}</span>
                <span className={`sync-queue-status ${item.status}`}>{item.status}</span>
                <span>{item.attempts}</span>
                <span className="sync-queue-error">{item.last_error || '-'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Seção de Notificações */}
      <div className="settings-section">
        <h2>🔔 Notificações</h2>
        <p className="section-description">
          Gerencie as notificações do aplicativo. Receba alertas sobre vencimentos, sincronizações e outras atividades importantes.
        </p>
        <div className="info-grid">
          <div className="info-item">
            <strong>Suporte:</strong>
            <span className={notificationsSupported ? 'status-ok' : 'status-error'}>
              {notificationsSupported ? '✅ Suportado' : '❌ Não suportado'}
            </span>
          </div>
          <div className="info-item">
            <strong>Permissão:</strong>
            <span className={notificationPermission === 'granted' ? 'status-ok' : notificationPermission === 'denied' ? 'status-error' : 'status-warning'}>
              {notificationPermission === 'granted' ? '✅ Permitido' : 
               notificationPermission === 'denied' ? '❌ Negado' : 
               '⚠️ Não solicitado'}
            </span>
          </div>
          <div className="info-item">
            <strong>Status:</strong>
            <span className={hasNotificationPermission ? 'status-ok' : 'status-error'}>
              {hasNotificationPermission ? '✅ Ativo' : '❌ Inativo'}
            </span>
          </div>
        </div>
        <div className="sync-actions" style={{ marginTop: '20px' }}>
          {notificationPermission !== 'granted' && (
            <button
              onClick={async () => {
                try {
                  await requestNotificationPermission();
                  setMessage('✅ Permissão de notificações solicitada!');
                  addLog('Permissão de notificações solicitada');
                } catch (error: any) {
                  setMessage(`❌ Erro ao solicitar permissão: ${error.message}`);
                  addLog(`Erro ao solicitar permissão: ${error.message}`);
                }
              }}
              className="action-button primary"
            >
              🔔 Solicitar Permissão de Notificações
            </button>
          )}
          {notificationPermission === 'granted' && (
            <div className="toggle-container" style={{ marginTop: '15px' }}>
              <label htmlFor="notifications-enabled" className="toggle-label">
                Habilitar Notificações
              </label>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  id="notifications-enabled"
                  checked={notificationsEnabled}
                  onChange={(e) => {
                    setNotificationsEnabled(e.target.checked);
                    setMessage(e.target.checked ? '✅ Notificações habilitadas' : '⚠️ Notificações desabilitadas');
                    addLog(`Notificações ${e.target.checked ? 'habilitadas' : 'desabilitadas'}`);
                  }}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          )}
        </div>
        {notificationPermission === 'denied' && (
          <div className="message error" style={{ marginTop: '15px' }}>
            ⚠️ As notificações foram negadas. Para habilitá-las, acesse as configurações do navegador e permita notificações para este site.
          </div>
        )}
      </div>

      {/* Seção de Debug - Informações do Usuário */}
      <div className="settings-section">
        <h2>👤 Informações do Usuário</h2>
        <div className="info-grid">
          <div className="info-item">
            <strong>Usuário:</strong>
            <span>{user ? `${user.name} (${user.email})` : 'Não autenticado'}</span>
          </div>
          <div className="info-item">
            <strong>ID:</strong>
            <span>{user?.id || 'N/A'}</span>
          </div>
          <div className="info-item">
            <strong>Admin:</strong>
            <span>{user?.is_admin ? '✅ Sim' : '❌ Não'}</span>
          </div>
        </div>
      </div>

      {/* Seção de Debug - Testes */}
      <div className="settings-section">
        <h2>🧪 Testes de Conectividade</h2>
        <div className="test-buttons">
          <button onClick={testBackendHealth} className="test-button">
            Testar Health Check
          </button>
          <button onClick={testBackendLogin} className="test-button">
            Testar Login
          </button>
          <button onClick={testBackendStatus} className="test-button">
            Verificar Status Backend
          </button>
          <button onClick={forceBackendStatusCheck} className="test-button">
            🔄 Forçar Verificação Backend
          </button>
          <button onClick={collectDebugInfo} className="test-button">
            Atualizar Informações
          </button>
        </div>
        
        {Object.keys(testResults).length > 0 && (
          <div className="test-results">
            <h3>Resultados dos Testes:</h3>
            <pre>{JSON.stringify(testResults, null, 2)}</pre>
          </div>
        )}
      </div>

      {/* Seção de Debug - Logs */}
      <div className="settings-section">
        <h2>📝 Logs</h2>
        <div className="logs-container">
          {logs.length === 0 ? (
            <p>Nenhum log ainda. Execute alguns testes para ver os logs.</p>
          ) : (
            logs.map((log, index) => (
              <div key={index} className="log-entry">
                {log}
              </div>
            ))
          )}
        </div>
        <button onClick={() => setLogs([])} className="clear-button">
          Limpar Logs
        </button>
      </div>

      {/* Seção de Debug - Informações Completas */}
      <div className="settings-section">
        <h2>📋 Informações Completas (JSON)</h2>
        <details>
          <summary>Clique para expandir</summary>
          <pre className="json-output">{JSON.stringify(debugInfo, null, 2)}</pre>
        </details>
      </div>

      {/* Seção de Atualização PWA */}
      <div className="settings-section">
        <h2>📱 Atualização do Aplicativo (PWA)</h2>
        <p className="section-description">
          O aplicativo verifica automaticamente por atualizações. Você também pode verificar manualmente.
        </p>
        
        {/* Informações de Versão */}
        <div className="info-grid" style={{ marginBottom: '20px' }}>
          <div className="info-item">
            <strong>Versão do Aplicativo:</strong>
            <span className="status-ok" style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {APP_VERSION}
            </span>
          </div>
          <div className="info-item">
            <strong>Versão do Cache (Service Worker):</strong>
            <span className="status-ok" style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {SW_CACHE_VERSION}
            </span>
          </div>
          <div className="info-item">
            <strong>Service Worker:</strong>
            <span className={navigator.serviceWorker ? 'status-ok' : 'status-error'}>
              {navigator.serviceWorker ? '✅ Ativo' : '❌ Não suportado'}
            </span>
          </div>
        </div>

        <div className="action-buttons">
          <button onClick={handleUpdatePWA} className="action-button primary" disabled={updatingPWA}>
            {updatingPWA ? 'Verificando...' : '🔄 Verificar Atualizações Agora'}
          </button>
        </div>
        <div style={{ marginTop: '15px', padding: '15px', background: '#f0f7ff', borderRadius: '6px', fontSize: '14px', color: '#333' }}>
          <strong>Como atualizar a PWA instalada:</strong>
          <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
            <li><strong>Atualização Automática:</strong> O app verifica atualizações automaticamente quando você abre o aplicativo ou quando a página ganha foco.</li>
            <li><strong>Notificação:</strong> Quando uma nova versão estiver disponível, um banner aparecerá na parte inferior da tela.</li>
            <li><strong>Atualizar:</strong> Clique em "Atualizar Agora" no banner ou use o botão acima para verificar manualmente.</li>
            <li><strong>Forçar Atualização:</strong> Se necessário, você pode desinstalar e reinstalar o aplicativo do seu dispositivo.</li>
          </ul>
        </div>
      </div>

      {/* Seção de Links Úteis */}
      <div className="settings-section">
        <h2>🔗 Links Úteis</h2>
        <div className="links">
          <Link to="/setup-backend-ip">
            Configurar IP do Backend
          </Link>
          <Link to="/dashboard">
            Voltar ao Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Settings;
